import {makeObservable, runInAction, toJS} from 'mobx';

import type {Nullable} from '../../../../utils/utility-types';
import type {MetricModel} from '../../Metric/Metric.model';

import {mergeDeepObj} from '../../../../utils/object/mergeDeepObj';
import {BaseModel} from '../../Base/Base.model';

const ModelName = 'UserMetadataModel';

export class UserMetadataModel extends BaseModel {
  b2c_demo?: {
    anxious?: string;
    anything_else?: string;
    coaching_style?: string;
    code?: string;
    partner_id?: string;
    support?: string;
    what_brings_you?: string;
    worrying?: string;
  };
  dtc_qrcode?: {
    age?: string;
    anxious?: string;
    areas_of_wellbeing?: string[];
    coaching_style?: string;
    obstacles?: string[];
    other_professionals?: string[];
    worrying?: string;
  };
  employee?: {
    anxious?: string;
    anything_else?: string;
    coaching_style?: string;
    support?: string;
    what_brings_you?: string;
    worrying?: string;
  };
  employer?: {
    code?: string;
    company_name?: string;
    invalid_code?: string;
    position?: string;
  };
  fd_onboarding?: {
    is_finished?: boolean;
  };
  is_b2c_demo_coach?: boolean;
  is_dtc?: boolean;
  last_used_coupon_id?: string;
  onboarding?: {
    account_type?: Nullable<UserMetadataModel.AccountType>;
    coupon_reservation?: Nullable<{
      code: string;
      used_coupon_id: string;
    }>;
    /** @type date.toISOString */
    finished?: Nullable<string>;
    invites_count?: number;
    is_finished?: boolean;
  };
  settings?: {
    dont_show_phone_number_request?: boolean;
    /** @type date.toISOString */
    phone_number_request_shown_at?: string;
    weight_unit?: MetricModel.WeightUnit;
  };
  tracking?: string[];

  constructor(props: UserMetadataModel.Props) {
    super({id: ''});
    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<
        UserMetadataModel,
        UserMetadataModel.Props
      >(props),
    );
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  override update(source: BaseModel.Source<UserMetadataModel>): this {
    const next = mergeDeepObj(toJS(this), source);

    runInAction(() => {
      for (const key in next) {
        this[key] = next[key];
      }
    });

    return this;
  }
}

export namespace UserMetadataModel {
  export type AccountType =
    | 'b2b_client'
    | 'b2c_demo_client'
    | 'client'
    | 'coach'
    | 'employee'
    | 'invited_client';

  export type Props = BaseModel.ModelToProps<UserMetadataModel, 'id'>;
}
